/**
 * Streams an existing microphone track to a callback as base64-encoded PCM16
 * chunks at 16 kHz — the format ElevenLabs Scribe's manual (`AudioOptions`)
 * mode expects. This lets us feed Scribe from the recorder's already-open mic
 * instead of the SDK's `microphone` option, which opens a *second*
 * `getUserMedia` (slow to come online, and a separate audio stream that has to
 * be re-aligned to the recording after the fact).
 *
 * The AudioContext is created at 16 kHz so the worklet only has to convert
 * Float32 samples to PCM16 — no resampling.
 */
const TARGET_SAMPLE_RATE = 16000;
const PROCESSOR_NAME = 'l5-scribe-pcm16';

// Samples accumulated in the worklet before a chunk is posted to the main
// thread. Batching (rather than posting every 128-sample render quantum) keeps
// the message + base64 rate to ~4/sec instead of ~125/sec
const WORKLET_CHUNK_SAMPLES = 4096;

/**
 * AudioWorklet that converts mono Float32 audio to PCM16
 */
const WORKLET_SOURCE = `
const CHUNK_SAMPLES = ${WORKLET_CHUNK_SAMPLES};
class Pcm16Processor extends AudioWorkletProcessor {
    constructor() {
        super();
        this._buffer = new Int16Array(CHUNK_SAMPLES);
        this._offset = 0;
    }
    process(inputs) {
        const input = inputs[0];
        const channel = input && input[0];
        if (channel && channel.length) {
            for (let i = 0; i < channel.length; i++) {
                const sample = Math.max(-1, Math.min(1, channel[i]));
                this._buffer[this._offset++] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
                if (this._offset === CHUNK_SAMPLES) {
                    const chunk = this._buffer.slice();
                    this.port.postMessage(chunk.buffer, [chunk.buffer]);
                    this._offset = 0;
                }
            }
        }
        return true;
    }
}
registerProcessor('${PROCESSOR_NAME}', Pcm16Processor);
`;

function pcm16ToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

export class ScribeAudioFeed {
    readonly #onChunk: (base64Pcm16: string) => void;

    #audioContext: AudioContext | null = null;
    #source: MediaStreamAudioSourceNode | null = null;
    #workletNode: AudioWorkletNode | null = null;
    #paused = false;

    constructor(onChunk: (base64Pcm16: string) => void) {
        this.#onChunk = onChunk;
    }

    /**
     * Begin capturing `micTrack` and emitting PCM16 chunks.
     */
    async start(micTrack: MediaStreamTrack): Promise<void> {
        const audioContext = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
        this.#audioContext = audioContext;

        const blobUrl = URL.createObjectURL(
            new Blob([WORKLET_SOURCE], { type: 'application/javascript' }),
        );
        try {
            await audioContext.audioWorklet.addModule(blobUrl);
        } finally {
            URL.revokeObjectURL(blobUrl);
        }

        const source = audioContext.createMediaStreamSource(new MediaStream([micTrack]));
        const workletNode = new AudioWorkletNode(audioContext, PROCESSOR_NAME);
        workletNode.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
            if (this.#paused) {
                return;
            }
            this.#onChunk(pcm16ToBase64(event.data));
        };

        source.connect(workletNode);
        this.#source = source;
        this.#workletNode = workletNode;

        if (audioContext.state === 'suspended') {
            await audioContext.resume();
        }
    }

    pause(): void {
        this.#paused = true;
    }

    resume(): void {
        this.#paused = false;
    }

    stop(): void {
        if (this.#workletNode) {
            this.#workletNode.port.onmessage = null;
            this.#workletNode.disconnect();
            this.#workletNode = null;
        }
        this.#source?.disconnect();
        this.#source = null;
        if (this.#audioContext && this.#audioContext.state !== 'closed') {
            void this.#audioContext.close();
        }
        this.#audioContext = null;
    }
}
